import React, { useState, useEffect, useRef } from 'react';
import { usePrevious } from "@uidotdev/usehooks";
import styled from 'styled-components';
import { Wrapper, Status } from "@googlemaps/react-wrapper";

import { tile2lon, tile2lat, ToLL, colorName } from '../../helpers';
import { DEMO_MACROLOTTI, DEMO_SUBAREE } from '../../demodata';

import Loading from '../../common/components/Loading';

let ZOOM_LV0 = 14;
let ZOOM_LV3 = 17;
let ZOOM_MAX = 18;
let INITIAL_LAT = 43.563000;
let INITIAL_LNG = 11.453000;
let GOOGLEMAPS_APIKEY = "AIzaSyDeVWT_7CdZ7FY3mxuBDNdV9gZU2nEKs1k";
let INTERVAL = {
	11: [[1088, 747], [1089, 748]],
	12: [[2177, 1495], [2179, 1497]],
	13: [[4355, 2991], [4359, 2994]],
	14: [[8711, 5982], [8719, 5989]],
	15: [[17421, 11965], [34878, 23958]],
	16: [[34843, 23930], [34878, 23958]],
	17: [[69686, 47861], [69756, 47917]],
	18: [[139372, 95722], [139512, 95835]]
};

const Container = styled.div.withConfig({ displayName: '-Container' })`
	position: absolute;
	top: 0;
	left: 550px;
	width: calc(100% - 1100px);
	height: 100%;
	background-color: #f1f1f1;
	overflow: hidden;
`;

const MapWrapper = styled.div.withConfig({ displayName: '-MapWrapper' })`
	position: absolute;
	top: 0;
	left: 0px;
	width: 100%;
	height: 100%;
`;

const LoaderContainer = styled.div.withConfig({ displayName: '-LoaderContainer' })`
	position: absolute;
	background-color: #f1f1f1;
	top: 0;
	left: 0px;
	width: 100%;
	height: 100%;
`;

/**
 * @template T
 * @typedef {[T, (v:T) => void]} State
 */

/**
 *
 * @param {*} param0
 * @returns
 */

const Map = ({
	mapType,
	showListCartografie,
	fullListLv1,
	showListLv1,
	lv1,
	setLv1,
	hoverLv1,
	setHoverLv1,
	fullListLv2,
	showListLv2,
	lv2,
	setLv2,
	hoverLv2,
	setHoverLv2,
	fullListLv3,
	showListLv3,
	lv3,
	setLv3,
	hoverLv3,
	setHoverLv3,
	setLv4
}) => {
	const [map, setMap] = useState(null);
	const [overlaysCartografie, setOverlaysCartografie] = useState([]);
	const [polygonsMacrolotti, setPolygonsMacrolotti] = useState([]);
	const [polygonsSubaree, setPolygonsSubaree] = useState([]);
	const [markersSondaggi, setMarkersSondaggi] = useState([]);
	const previousHoverLv1 = usePrevious(hoverLv1);
	const previousHoverLv2 = usePrevious(hoverLv2);
	const previousHoverLv3 = usePrevious(hoverLv3);

	const ref = useRef();

	useEffect(() => {
		if (fullListLv3.length > 0 && map === null) {
			let initedPolygonsMacrolotti = [];
			let initedPolygonsSubaree = [];
			let initedMarkersSondaggi = [];
			let initedOverlaysCartografie = [];
			let initedMap = new window.google.maps.Map(ref.current, {
				zoom: ZOOM_LV0,
				maxZoom: ZOOM_MAX,
				center: { lat: INITIAL_LAT, lng: INITIAL_LNG },
				gestureHandling: 'greedy',
				mapTypeControl: false,
				streetViewControl: false,
				styles: [
					{
						featureType: "poi",
						stylers: [
							{ visibility: "off" }
						]
					}, {
						featureType: "transit",
						stylers: [
							{ visibility: "off" }
						]
					}
				]
			});

			/* MACROLOTTI */
			Object.keys(DEMO_MACROLOTTI).forEach(function (keyMacrolotto) {
				initedPolygonsMacrolotti[keyMacrolotto] = [];
				initedPolygonsMacrolotti[keyMacrolotto].bounds = new window.google.maps.LatLngBounds();
				for (let i = 0; i < DEMO_MACROLOTTI[keyMacrolotto].coords.length; i++) {
					initedPolygonsMacrolotti[keyMacrolotto].bounds.extend(DEMO_MACROLOTTI[keyMacrolotto].coords[i]);
				}
				initedPolygonsMacrolotti[keyMacrolotto].gPolygon = new window.google.maps.Polygon({
					paths: DEMO_MACROLOTTI[keyMacrolotto].coords,
					strokeColor: '#0d609d',
					strokeWeight: 2,
					fillColor: '#0d609d',
					strokeOpacity: 0.8,
					fillOpacity: 0.35
				});
				initedPolygonsMacrolotti[keyMacrolotto].gPolygon.setMap(initedMap);
				initedPolygonsMacrolotti[keyMacrolotto].gPolygon.setOptions({ zIndex: 2 });
				initedPolygonsMacrolotti[keyMacrolotto].gPolygon.setVisible(showListLv1.map(area => area.id).includes(keyMacrolotto));

				initedPolygonsMacrolotti[keyMacrolotto].gInfo = new window.google.maps.InfoWindow({
					content: DEMO_MACROLOTTI[keyMacrolotto].name,
					position: DEMO_MACROLOTTI[keyMacrolotto].center,
					disableAutoPan: true
				});

				window.google.maps.event.addListener(initedPolygonsMacrolotti[keyMacrolotto].gPolygon, 'click', function () {
					setLv1(fullListLv1.filter(item => item.id === keyMacrolotto)[0]);
					setLv2(null);
					setLv3(null);
					setLv4(null);
				});

				window.google.maps.event.addListener(initedPolygonsMacrolotti[keyMacrolotto].gPolygon, 'mouseover', function () {
					setHoverLv1(fullListLv1.filter(item => item.id === keyMacrolotto)[0]);
				});

				window.google.maps.event.addListener(initedPolygonsMacrolotti[keyMacrolotto].gPolygon, 'mouseout', function () {
					setHoverLv1(null);
				});
			});

			/* SUBAREE */
			Object.keys(DEMO_SUBAREE).forEach(function (keySubarea) {
				initedPolygonsSubaree[keySubarea] = [];
				initedPolygonsSubaree[keySubarea].bounds = new window.google.maps.LatLngBounds();
				for (let i = 0; i < DEMO_SUBAREE[keySubarea].coords.length; i++) {
					initedPolygonsSubaree[keySubarea][i] = {};
					for (var j = 0; j < DEMO_SUBAREE[keySubarea].coords[i].length; j++) {
						initedPolygonsSubaree[keySubarea].bounds.extend(DEMO_SUBAREE[keySubarea].coords[i][j]);
					}
					initedPolygonsSubaree[keySubarea][i].gPolygon = new window.google.maps.Polygon({
						paths: DEMO_SUBAREE[keySubarea].coords[i],
						strokeColor: '#6224eb',
						strokeOpacity: 0.8,
						strokeWeight: 2,
						fillColor: '#6224eb',
						fillOpacity: 0.35
					});
					initedPolygonsSubaree[keySubarea][i].gPolygon.setMap(initedMap);
					initedPolygonsSubaree[keySubarea][i].gPolygon.setOptions({ zIndex: 3 });
					initedPolygonsSubaree[keySubarea].forEach((area =>
						area.gPolygon.setVisible(showListLv2.map(area => area.id).includes(keySubarea))
					));

					initedPolygonsSubaree[keySubarea].gInfo = new window.google.maps.InfoWindow({
						content: DEMO_SUBAREE[keySubarea].name,
						position: DEMO_SUBAREE[keySubarea].center,
						disableAutoPan: true
					});

					window.google.maps.event.addListener(initedPolygonsSubaree[keySubarea][i].gPolygon, 'click', function () {
						setLv1(fullListLv1.filter(item => item.id === (
							fullListLv2.filter(item => item.id === keySubarea)[0].macrolottoId
						))[0]);
						setLv2(fullListLv2.filter(item => item.id === keySubarea)[0]);
						setLv3(null);
						setLv4(null);
					});

					window.google.maps.event.addListener(initedPolygonsSubaree[keySubarea][i].gPolygon, 'mouseover', function () {
						setHoverLv2(fullListLv2.filter(item => item.id === keySubarea)[0]);
					});

					window.google.maps.event.addListener(initedPolygonsSubaree[keySubarea][i].gPolygon, 'mouseout', function () {
						setHoverLv2(null);
					});
				}
			});

			/* CAMPIONI */
			initedMarkersSondaggi = [...fullListLv3]
			if (initedMarkersSondaggi.length > 0) {
				initedMarkersSondaggi.forEach(function (marker) {

					let icon = getMarkerIconPath(marker);


					marker.gMarker = new window.google.maps.Marker({
						position: ToLL(marker.y_utm, marker.x_utm, 32),//{lat: parseFloat(marker.x_utm), lng: parseFloat(marker.y_utm)},
						// label: objMarker.name,
						icon: {
							url: icon,
							size: new window.google.maps.Size(41, 41),
							origin: new window.google.maps.Point(0, 0),
							anchor: new window.google.maps.Point(21, 21),
							scaledSize: new window.google.maps.Size(41, 41)
						},
						shape: {
							type: marker.coloreSondaggio === 11 ? 'square' : 'circle',
							coords: [21, 21, 12]
						},
						map: initedMap,
						// title: objMarker.name,
						alt: ""
					});

					marker.gInfo = new window.google.maps.InfoWindow({
						content: marker.siglaSondaggio ?? marker.nome ?? 'n.a.',
						disableAutoPan: true,
						pixelOffset: new window.google.maps.Size(0, 10)
					});

					window.google.maps.event.addListener(marker.gMarker, 'click', function () {
						setLv1(fullListLv1.filter(item => item.id === marker.macrolottoId)[0]);
						setLv2(fullListLv2.filter(item => item.id === marker.subareaId)[0]);
						setLv3(fullListLv3.filter(item => item.id === marker.id)[0]);
						setLv4(null);
					});

					window.google.maps.event.addListener(marker.gMarker, 'mouseover', function () {
						setHoverLv3(fullListLv3.filter(item => item.id === marker.id)[0]);
					});

					window.google.maps.event.addListener(marker.gMarker, 'mouseout', function () {
						setHoverLv3(null);
					});

				});
			}

			/* CARTOGRAFIE */
			var bounds = INTERVAL;

			initedOverlaysCartografie["ctr10k"] = new window.google.maps.ImageMapType({
				getTileUrl: function (coord, zoom) {
					let lat2 = tile2lat(coord.y, zoom);
					let lon1 = tile2lon(coord.x, zoom);
					let lat1 = tile2lat(coord.y + 1, zoom);
					let lon2 = tile2lon(coord.x + 1, zoom);
					return "http://www502.regione.toscana.it/ows_ctr/com.rt.wms.RTmap/ows?map=owsctr&FORMAT=image%2Fpng&LAYERS=rt_ctr.10k&TRANSPARENT=true&ANTIALIAS=none&OPACITY=0&SERVICE=WMS&VERSION=1.1.1&REQUEST=GetMap&STYLES=&SRS=EPSG%3A4326&BBOX=" + lon1 + "," + lat1 + "," + lon2 + "," + lat2 + "&WIDTH=256&HEIGHT=256";
				},
				tileSize: new window.google.maps.Size(256, 256)
			});

			initedMap.overlayMapTypes.push(initedOverlaysCartografie["ctr10k"]);
			initedOverlaysCartografie["ctr10k"].setOpacity(0);

			// TODO does this work??
			initedOverlaysCartografie["ucs78"] = new window.google.maps.ImageMapType({
				getTileUrl: function (coord, zoom) {
					let lat2 = tile2lat(coord.y, zoom);
					let lon1 = tile2lon(coord.x, zoom);
					let lat1 = tile2lat(coord.y + 1, zoom);
					let lon2 = tile2lon(coord.x + 1, zoom);
					return "http://www502.regione.toscana.it/wmsraster/com.rt.wms.RTmap/wms?map=wmsucs&FORMAT=image%2Fpng&LAYERS=rt_ucs.iducs.25k.1978.rt&TRANSPARENT=true&ANTIALIAS=none&OPACITY=0&SERVICE=WMS&VERSION=1.1.1&REQUEST=GetMap&STYLES=&SRS=EPSG%3A4326&BBOX=" + lon1 + "," + lat1 + "," + lon2 + "," + lat2 + "&WIDTH=256&HEIGHT=256";
				},
				tileSize: new window.google.maps.Size(256, 256)
			});

			initedMap.overlayMapTypes.push(initedOverlaysCartografie["ucs78"]);
			initedOverlaysCartografie["ucs78"].setOpacity(0);

			initedOverlaysCartografie["cgt"] = new window.google.maps.ImageMapType({
				getTileUrl: function (coord, zoom) {
					if (bounds[zoom] === undefined ||
						bounds[zoom][0][0] > coord.x || coord.x > bounds[zoom][1][0] ||
						bounds[zoom][0][1] > coord.y || coord.y > bounds[zoom][1][1]) {
						return null;
					}
					var ymax = 1 << zoom;
					var y = ymax - coord.y - 1;
					return "https://d1ig7l4vjhqa31.cloudfront.net/cesi/1/" + zoom + '/' + coord.x + '/' + y + '.png';
				},
				tileSize: new window.google.maps.Size(256, 256)
			});

			initedMap.overlayMapTypes.push(initedOverlaysCartografie["cgt"]);
			initedOverlaysCartografie["cgt"].setOpacity(0);

			setMap(initedMap);
			setPolygonsMacrolotti(initedPolygonsMacrolotti);
			setPolygonsSubaree(initedPolygonsSubaree);
			setMarkersSondaggi(initedMarkersSondaggi);
			setOverlaysCartografie(initedOverlaysCartografie);
		}
	}, [map, fullListLv1, showListLv1, fullListLv2, showListLv2, fullListLv3, showListLv3,

		// these are actually state setters and should not be here, but the linter cannot
		// figure it out since they are passed as props
		setHoverLv1, setHoverLv2, setHoverLv3, setLv1, setLv2, setLv3, setLv4
	]);


	/*LV1*/
	useEffect(() => {
		if (map && hoverLv1 && (!lv1 || hoverLv1.id !== lv1.id)) {
			polygonsMacrolotti[hoverLv1.id].gInfo.open(map);
			polygonsMacrolotti[hoverLv1.id].gPolygon.setOptions({
				strokeColor: 'red',
				fillColor: 'red',
			});
			setHoverLv2(null);
			setHoverLv3(null);
		}
	}, [map, lv1, hoverLv1, polygonsMacrolotti,
		setHoverLv2, setHoverLv3
	]);

	useEffect(() => {
		if (map && previousHoverLv1 && (!lv1 || previousHoverLv1.id !== lv1.id)) {
			polygonsMacrolotti[previousHoverLv1.id].gInfo.close();
			polygonsMacrolotti[previousHoverLv1.id].gPolygon.setOptions({
				strokeColor: '#0d609d',
				fillColor: '#0d609d'
			});
		}
	}, [map, lv1, previousHoverLv1, polygonsMacrolotti]);

	useEffect(() => {
		if (map) {
			polygonsMacrolotti.forEach(poligono => {
				poligono.gPolygon.setOptions({
					strokeColor: '#0d609d',
					fillColor: '#0d609d'
				});
			})

			if (lv1 && !lv2) {
				polygonsMacrolotti[lv1.id].gInfo.close();
				map.fitBounds(polygonsMacrolotti[lv1.id].bounds);
				polygonsMacrolotti[lv1.id].gPolygon.setOptions({
					strokeColor: 'red',
					fillColor: 'red',
				});
			} else {
				map.setZoom(ZOOM_LV0);
				map.panTo({ lat: INITIAL_LAT, lng: INITIAL_LNG });
				// resetMapSelections();
				// resetMapInfoboxes();
			}
		}
	}, [map, lv1, lv2, polygonsMacrolotti]);

	useEffect(() => {
		if (map) {
			Object.keys(polygonsMacrolotti).forEach(keyMacrolotto => {
				polygonsMacrolotti[keyMacrolotto].gPolygon.setVisible(showListLv1.map(area => area.id).includes(keyMacrolotto));
			});
		}
	}, [map, showListLv1, polygonsMacrolotti]);

	/*LV2*/
	useEffect(() => {
		// polygonsMacrolotti[id].gInfo.open(map);
		if (map && hoverLv2 && polygonsSubaree[hoverLv2.id] && (!lv2 || hoverLv2.id !== lv2.id)) {
			polygonsSubaree[hoverLv2.id].gInfo.open(map);
			polygonsSubaree[hoverLv2.id].forEach((area =>
				area.gPolygon.setOptions({
					strokeColor: 'yellow',
					fillColor: 'yellow',
				})
			));
			setHoverLv1(null);
			setHoverLv3(null);
		}
	}, [map, lv2, hoverLv2, polygonsSubaree,
		setHoverLv1, setHoverLv3
	]);

	useEffect(() => {
		// polygonsMacrolotti[id].gInfo.open(map);
		if (map && previousHoverLv2 && polygonsSubaree[previousHoverLv2.id] && (!lv2 || previousHoverLv2.id !== lv2.id)) {
			polygonsSubaree[previousHoverLv2.id].gInfo.close();
			polygonsSubaree[previousHoverLv2.id].forEach((area =>
				area.gPolygon.setOptions({
					strokeColor: '#6224eb',
					fillColor: '#6224eb'
				})
			));
		}
	}, [map, lv2, previousHoverLv2, polygonsSubaree]);

	useEffect(() => {
		if (map) {
			polygonsSubaree.forEach(poligono => {
				poligono.forEach((area =>
					area.gPolygon.setOptions({
						strokeColor: '#6224eb',
						fillColor: '#6224eb'
					})
				))
			});

			if (lv2 && !lv3) {
				if (polygonsSubaree[lv2.id]) {
					polygonsSubaree[lv2.id].gInfo.close();
					map.fitBounds(polygonsSubaree[lv2.id].bounds);
					polygonsSubaree[lv2.id].forEach((area =>
						area.gPolygon.setOptions({
							strokeColor: 'yellow',
							fillColor: 'yellow',
						})
					));
				}
			}
		}
	}, [map, lv2, lv3, polygonsSubaree]);

	useEffect(() => {
		if (map) {
			Object.keys(polygonsSubaree).forEach(keySubarea => {
				polygonsSubaree[keySubarea].forEach((area =>
					area.gPolygon.setVisible(showListLv2.map(area => area.id).includes(keySubarea))
				));
			});
		}
	}, [map, showListLv2, polygonsSubaree]);

	/*LV3*/
	useEffect(() => {
		if (map && hoverLv3) {
			markersSondaggi.filter(marker => marker.id === hoverLv3.id && marker.gInfo != null)[0].gInfo.open(map, markersSondaggi.filter(marker => marker.id === hoverLv3.id)[0].gMarker);
			setHoverLv1(null);
			setHoverLv2(null);
		}
	}, [map, hoverLv3, markersSondaggi,
		setHoverLv1, setHoverLv2
	]);

	useEffect(() => {
		if (map && previousHoverLv3 && (!lv3 || previousHoverLv3.id !== lv3.id)) {
			markersSondaggi.filter(marker => marker.id === previousHoverLv3.id)[0].gInfo.close();
		}
	}, [map, lv3, previousHoverLv3, markersSondaggi]);

	useEffect(() => {
		if (map) {
			markersSondaggi.forEach(marker => {
				marker.gInfo.close();
			})
			markersSondaggi.forEach(marker => {
				marker.gMarker.setIcon({
					url: marker.gMarker.getIcon().url.replace("-on", "-off")
				});
				marker.gMarker.setShape({
					type: 'circle',
					coords: [21, 21, 12]
				});
			});

			if (lv3) {
				markersSondaggi.filter(marker => marker.id === lv3.id)[0].gInfo.open(map, markersSondaggi.filter(marker => marker.id === lv3.id)[0].gMarker);
				map.setZoom(ZOOM_LV3);
				map.panTo(markersSondaggi.filter(marker => marker.id === lv3.id)[0].gMarker.position);
				markersSondaggi.filter(marker => marker.id === lv3.id)[0].gMarker.setIcon({
					url: markersSondaggi.filter(marker => marker.id === lv3.id)[0].gMarker.getIcon().url.replace("-off", "-on")
				});
				markersSondaggi.filter(marker => marker.id === lv3.id)[0].gMarker.setShape({
					type: 'circle',
					coords: [21, 21, 16]
				});
			}
		}
	}, [map, lv3, markersSondaggi]);

	// useEffect(() => {
	// 	Object.keys(polygonsMacrolotti).forEach(keyMacrolotto => {
	// 		polygonsMacrolotti[keyMacrolotto].gPolygon.setVisible(showListLv1.map(area => area.id).includes(keyMacrolotto));
	// 	});
	// }, [showListLv1, polygonsMacrolotti]);


	/*TERRENI*/
	useEffect(() => {
		if (map) {
			switch (mapType) {
				case "terrain":
				default:
					map.setMapTypeId(window.google.maps.MapTypeId.TERRAIN);
					break;
				case "satellite":
					map.setMapTypeId(window.google.maps.MapTypeId.SATELLITE);
					break;
				case "roadmap":
					map.setMapTypeId(window.google.maps.MapTypeId.ROADMAP);
					break;
				case "hybrid":
					map.setMapTypeId(window.google.maps.MapTypeId.HYBRID);
					break;
			}
		}
	}, [map, mapType]);

	useEffect(() => {
		if (map) {
			["ctr10k", "ucs78", "cgt"].forEach(cartografia => {
				if (showListCartografie.includes(cartografia)) {
					overlaysCartografie[cartografia].setOpacity(100);
				} else {
					overlaysCartografie[cartografia].setOpacity(0);
				}
			});
		}
	}, [map, showListCartografie, overlaysCartografie]);


	return <>
		{!map ?
			<LoaderContainer><Loading /></LoaderContainer>
			: null}
		<MapWrapper ref={ref} id="map" width="100%" height="100%" />
	</>;

};


const GMap = ({
	mapType,
	showListCartografie,
	fullListLv1,
	showListLv1,
	lv1,
	setLv1,
	hoverLv1,
	setHoverLv1,
	fullListLv2,
	showListLv2,
	lv2,
	setLv2,
	hoverLv2,
	setHoverLv2,
	fullListLv3,
	showListLv3,
	lv3,
	setLv3,
	hoverLv3,
	setHoverLv3,
	setLv4
}) => {

	return (
		<Container>
			<Wrapper
				apiKey={GOOGLEMAPS_APIKEY}
				render={(status) => {
					switch (status) {
						case Status.FAILURE:
							return "ERROR";//<ErrorComponent />;
						case Status.SUCCESS:
							return <Map
								mapType={mapType}
								showListCartografie={showListCartografie}
								fullListLv1={fullListLv1}
								showListLv1={showListLv1}
								lv1={lv1}
								setLv1={setLv1}
								hoverLv1={hoverLv1}
								setHoverLv1={setHoverLv1}
								fullListLv2={fullListLv2}
								showListLv2={showListLv2}
								lv2={lv2}
								setLv2={setLv2}
								hoverLv2={hoverLv2}
								setHoverLv2={setHoverLv2}
								fullListLv3={fullListLv3}
								showListLv3={showListLv3}
								lv3={lv3}
								setLv3={setLv3}
								hoverLv3={hoverLv3}
								setHoverLv3={setHoverLv3}
								setLv4={setLv4}
							/>;
						case Status.LOADING:
						default:
							return null;
					}
				}}
			/>
		</Container>
	)
}

export default GMap

function getMarkerIconPath(marker) {
	let icon = "/assets/markers/marker-";

	if (marker.tipo == 'Piezómetro') {
		icon = '/assets/markers/piezo-sm.png';
	}
	else {
		const col = colorName(marker.coloreSondaggio);
		if (marker.has_terreno && marker.has_eluato) {
			icon += col + "-e-off.png";
		} else if (marker.has_terreno && !marker.has_eluato) {
			icon += col + "-t-off.png";
		} else if (!marker.has_terreno && marker.has_eluato) {
			icon += col + "-e-off.png";
		} else {
			icon += "gray-t-off.png";
		}
	}
	return icon;
}

